/** Draft Privacy Policy (EN) — validate with counsel before relying on as final legal text. */
export const privacyEn = {
  title: 'Privacy Policy',
  lastUpdatedLabel: 'Last updated',
  lastUpdated: 'July 27, 2026',
  sections: [
    {
      heading: '1. Data controller',
      paragraphs: [
        'This Privacy Policy explains how Rentalyzer / DMII (“we”, “us”) collects, uses, discloses, and protects personal information in connection with the Rentalyzer platform (the “Service”).',
        'For privacy requests: mmarceau@dmii.ca.',
      ],
    },
    {
      heading: '2. Scope',
      paragraphs: [
        'This Policy applies to use of the Service (website and related applications), including account creation, property analysis, deal sharing, and Service-related communications. It is designed to align with Québec’s Law 25 and Canada’s PIPEDA, as applicable.',
      ],
    },
    {
      heading: '3. Information we collect',
      paragraphs: [
        'Depending on your use, we may collect:',
      ],
      list: [
        'Identity and account: email address, display name, password (handled by the authentication provider), profile photo if any, language, account type.',
        'Contact and profile: phone, address, company name (especially if a professional module is requested).',
        'Real-estate analysis data: property addresses and characteristics, prices, rents, expenses, financing parameters, scenarios, images, and marketing descriptions you enter.',
        'Documents and files: project documents, contracts, specimens, or other files you upload.',
        'Sharing and interactions: share links, recipients, messages, interests, invitations, campaigns (where applicable).',
        'Technical data: access logs, device/browser type, approximate IP addresses, session tokens, and diagnostic data needed for operation and security (notably via Firebase / Google).',
        'Communications: transactional emails (password reset, invitations, notifications) and support messages.',
      ],
    },
    {
      heading: '4. Purposes',
      paragraphs: [
        'We use personal information to:',
      ],
      list: [
        'provide, maintain, and secure the Service;',
        'create and manage your account, authenticate users, and handle support requests;',
        'enable analyses, reports, sharing, and collaborations you initiate;',
        'send transactional communications related to the Service;',
        'prevent fraud, abuse, and security incidents;',
        'comply with legal obligations;',
        'improve the Service and produce statistics, indicators, and market insights from anonymized or aggregated data (see section 5).',
      ],
    },
    {
      heading: '5. Market data, anonymization, and aggregation',
      paragraphs: [
        'One of Rentalyzer’s goals is to better understand the rental real-estate market. To that end, we may use analysis and deal data you enter (for example prices, rents, expenses, rates, property typology, location at an appropriate granularity) to generate statistics, market indicators, and models after anonymization and/or aggregation.',
        'We aim to process these uses so that published or internally used datasets cannot, under reasonable conditions, re-identify a specific person or property. Raw named account data is not sold as a nominative marketing list.',
        'By accepting this Policy at signup, you consent to this secondary anonymized or aggregated use, subject to applicable law and your rights described below.',
      ],
    },
    {
      heading: '6. Legal bases and consent',
      paragraphs: [
        'We process personal information when needed to provide the Service you request, for legitimate interests related to security and Service improvement (within legal limits), to comply with law, or with your consent when required (for example at signup).',
        'You may withdraw certain consents where the law allows; withdrawal may limit feature use. Withdrawal does not affect the lawfulness of processing before withdrawal.',
      ],
    },
    {
      heading: '7. Disclosure to third parties and processors',
      paragraphs: [
        'We may use providers to host and operate the Service, including:',
      ],
      list: [
        'Google Firebase (authentication, database, storage, cloud functions);',
        'Google / Vertex AI (when assistance or document-extraction features are used);',
        'Resend or other transactional email providers;',
        'Google Maps / Places (address entry), where applicable;',
        'web hosts (e.g. cPanel server) for delivering the interface.',
      ],
      paragraphsAfterList: [
        'These providers process data on our behalf under their terms and security measures. We may also disclose information if required by law, to protect our rights, or at your direction (e.g. sharing a deal with a recipient you choose).',
      ],
    },
    {
      heading: '8. Transfers outside Québec / Canada',
      paragraphs: [
        'Some providers (notably Google / Firebase) may process data in the United States or other jurisdictions. When such transfers occur, we rely on appropriate contractual and organizational safeguards where possible. By using the Service, you acknowledge that your data may be processed outside Québec.',
      ],
    },
    {
      heading: '9. Retention',
      paragraphs: [
        'We retain personal information as long as needed to provide the Service, meet legal obligations, resolve disputes, and enforce our agreements. Anonymized or aggregated data may be kept longer because it is no longer identifiable personal information.',
        'When you request account deletion, we will delete or anonymize associated data according to our technical procedures, subject to backups and legal retention duties.',
      ],
    },
    {
      heading: '10. Security',
      paragraphs: [
        'We implement reasonable technical and organizational measures (access control, HTTPS encryption in transit, database access rules, etc.). No system is fully secure; you must also protect your credentials and devices.',
      ],
    },
    {
      heading: '11. Cookies and local storage',
      paragraphs: [
        'The Service uses browser storage (e.g. localStorage / sessionStorage) and cookies or similar technologies needed for authentication, session, language, and proper app operation. These technologies are not used here for third-party behavioural advertising.',
        'You may configure your browser to limit certain storage; that may affect Service functionality.',
      ],
    },
    {
      heading: '12. Your rights',
      paragraphs: [
        'Subject to applicable law (including Law 25 and PIPEDA), you may request access to or correction of your personal information, or exercise other rights (consent withdrawal, complaint, etc.). Contact mmarceau@dmii.ca. We may verify your identity before responding.',
        'You may also file a complaint with the Commission d’accès à l’information du Québec or the Office of the Privacy Commissioner of Canada, as applicable.',
      ],
    },
    {
      heading: '13. Children',
      paragraphs: [
        'The Service is not directed to individuals under 18. We do not knowingly collect personal information from children.',
      ],
    },
    {
      heading: '14. Changes',
      paragraphs: [
        'We may update this Policy. The update date and applicable version will be shown on this page. For material changes, we may also notify you through the Service or by email.',
      ],
    },
    {
      heading: '15. Contact',
      paragraphs: [
        'Rentalyzer / DMII — mmarceau@dmii.ca — https://rentalyzer.ca',
      ],
    },
  ],
};
